/**
 * Player Report PDF Generation Utility
 *
 * Generates a professional PDF report containing player data including:
 * - Player identity and key metrics
 * - AI summaries (player and scouting)
 * - Scouting reports (up to 4)
 * - Notes (up to 6)
 * - Individual metrics (passing, dribbling, etc.)
 * - Performance charts (spider graph and dot plot)
 * @remarks Uses jsPDF with lazy loading to minimize bundle impact.
 * Web-only implementation - native platforms not currently supported.
 */

/* eslint-disable code-organization/enforce-statement-order -- PDF generation is inherently procedural; draw functions both mutate state and return Y positions for sequential layout */

import { format, isValid, parseISO } from "date-fns";
import { Platform } from "react-native";

import type { PlayerNote, ScoutingReport } from "../types";

/**
 * Formats an ISO date string as DD/MM/YYYY, falling through to the raw value
 * when parsing fails so malformed input is visible rather than thrown.
 * The leading YYYY-MM-DD portion is isolated before parsing so timezoned
 * inputs like "2024-01-15T00:00:00.000Z" render as the intended calendar
 * date regardless of the viewer's local timezone.
 * @param value - ISO 8601 date string (may be null/undefined)
 * @returns Formatted date, the raw value when unparseable, or empty string
 */
function formatPdfDate(value: string | null | undefined): string {
  if (!value) return "";
  const datePortion = /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1] ?? value;
  const parsed = parseISO(datePortion);
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : value;
}

/**
 * Player identity data for PDF header section
 */
export interface PlayerIdentityPdfData {
  readonly playerName: string;
  readonly positions: string;
  readonly clubName: string | null;
  readonly age: string | null;
  readonly nationality: string | null;
  readonly agency: string | null;
}

/**
 * Key metrics data for PDF metrics section
 */
export interface PlayerMetricsPdfData {
  readonly gpr: string;
  readonly playerStyleFit: string;
  readonly teamStyleFit: string;
  readonly transferValue: string;
  readonly contractMonths: string;
}

/**
 * Chart image data for PDF chart sections
 */
export interface ChartImageData {
  readonly spiderChart: string | null;
  readonly dotPlot: string | null;
}

/**
 * Individual metric data for PDF distribution section
 */
export interface IndividualMetricPdfData {
  readonly label: string;
  readonly value: number;
}

/**
 * Complete data structure for PDF generation
 */
export interface PlayerReportPdfData {
  readonly identity: PlayerIdentityPdfData;
  readonly metrics: PlayerMetricsPdfData;
  readonly aiPlayerSummary: string | null;
  readonly aiScoutingSummary: string | null;
  readonly scoutingReports: readonly ScoutingReport[];
  readonly notes: readonly PlayerNote[];
  readonly individualMetrics: readonly IndividualMetricPdfData[];
  readonly charts: ChartImageData;
}

/** PDF page dimensions (A4 in points) */
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

/** Section spacing */
const SECTION_GAP = 20;
const LINE_HEIGHT = 14;

/** Colors */
const PRIMARY_COLOR: [number, number, number] = [34, 211, 238];
const TEXT_COLOR: [number, number, number] = [31, 41, 55];
const MUTED_COLOR: [number, number, number] = [107, 114, 128];
const BORDER_COLOR: [number, number, number] = [229, 231, 235];

/**
 * Draws a horizontal divider line
 * @param pdf - jsPDF document instance
 * @param yPos - Current Y position
 * @returns New Y position after divider
 */
function drawDivider(pdf: import("jspdf").jsPDF, yPos: number): number {
  pdf.setDrawColor(...BORDER_COLOR);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, yPos, PAGE_WIDTH - MARGIN, yPos);
  return yPos + 10;
}

/**
 * Draws a section header with cyan accent
 * @param pdf - jsPDF document instance
 * @param title - Section title text
 * @param yPos - Current Y position
 * @returns New Y position after header
 */
function drawSectionHeader(
  pdf: import("jspdf").jsPDF,
  title: string,
  yPos: number
): number {
  pdf.setFillColor(...PRIMARY_COLOR);
  pdf.rect(MARGIN, yPos, 4, 16, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text(title, MARGIN + 12, yPos + 12);

  return yPos + 32;
}

/**
 * Wraps text to fit within a given width and returns lines
 * @param pdf - jsPDF document instance
 * @param text - Text to wrap
 * @param maxWidth - Maximum width for text
 * @returns Array of wrapped text lines
 */
function wrapText(
  pdf: import("jspdf").jsPDF,
  text: string,
  maxWidth: number
): string[] {
  return pdf.splitTextToSize(text, maxWidth);
}

/**
 * Draws multi-line text and returns new Y position
 * @param pdf - jsPDF document instance
 * @param text - Text to draw
 * @param x - X position for text
 * @param yPos - Current Y position
 * @param maxWidth - Maximum width for text wrapping
 * @param lineHeight - Height between lines
 * @returns New Y position after text
 */
function drawWrappedText(
  pdf: import("jspdf").jsPDF,
  text: string,
  x: number,
  yPos: number,
  maxWidth: number,
  lineHeight: number = LINE_HEIGHT
): number {
  const lines = wrapText(pdf, text, maxWidth);
  lines.forEach((line: string, index: number) => {
    pdf.text(line, x, yPos + index * lineHeight);
  });
  return yPos + lines.length * lineHeight;
}

/**
 * Checks if we need a new page and adds one if necessary
 * @param pdf - jsPDF document instance
 * @param currentY - Current Y position
 * @param requiredSpace - Space required for next content
 * @returns Y position (new page position if page break occurred)
 */
function checkPageBreak(
  pdf: import("jspdf").jsPDF,
  currentY: number,
  requiredSpace: number
): number {
  if (currentY + requiredSpace > PAGE_HEIGHT - MARGIN) {
    pdf.addPage();
    return MARGIN + 20;
  }
  return currentY;
}

/**
 * Draws the report header with player name and generation date
 * @param pdf - jsPDF document instance
 * @param playerName - Player name to display
 * @returns Y position after header
 */
function drawReportHeader(
  pdf: import("jspdf").jsPDF,
  playerName: string
): number {
  // 1. DEFINITIONS
  const startY = MARGIN;
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const afterTitleY = startY + 50;
  // Pre-calculate the Y after divider (divider adds 10pt)
  const afterDividerY = afterTitleY + 10;

  // 2. SIDE EFFECTS - Draw title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text("Player Report", MARGIN, startY + 20);

  // Draw date
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text(`Generated: ${dateStr}`, MARGIN, startY + 35);

  // Draw divider (ignore return, we pre-calculated)
  drawDivider(pdf, afterTitleY);

  // Draw player name
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text(playerName, MARGIN, afterDividerY + 16);

  // 3. RETURN
  return afterDividerY + 30;
}

/**
 * Draws player identity section
 * @param pdf - jsPDF document instance
 * @param identity - Player identity data
 * @param yPos - Current Y position
 * @returns New Y position after identity section
 */
function drawIdentitySection(
  pdf: import("jspdf").jsPDF,
  identity: PlayerIdentityPdfData,
  yPos: number
): number {
  // 1. DEFINITIONS
  const details = [
    identity.positions,
    identity.age ? `Age ${identity.age}` : null,
    identity.clubName,
    identity.nationality,
    identity.agency ? `Agency: ${identity.agency}` : null,
  ]
    .filter(Boolean)
    .join("  |  ");

  // 2. SIDE EFFECTS
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text(details, MARGIN, yPos);

  // 3. RETURN
  return yPos + 20;
}

/**
 * Draws key metrics section in a grid layout
 * @param pdf - jsPDF document instance
 * @param metrics - Player metrics data
 * @param yPos - Current Y position
 * @returns New Y position after metrics section
 */
function drawMetricsSection(
  pdf: import("jspdf").jsPDF,
  metrics: PlayerMetricsPdfData,
  yPos: number
): number {
  const afterHeaderY = drawSectionHeader(pdf, "KEY METRICS", yPos);

  const metricsData = [
    { label: "GPR", value: metrics.gpr },
    { label: "Player Style Fit", value: metrics.playerStyleFit },
    { label: "Team Style Fit", value: metrics.teamStyleFit },
    { label: "Transfer Value", value: metrics.transferValue },
    { label: "Contract", value: `${metrics.contractMonths} months` },
  ];

  const colWidth = CONTENT_WIDTH / 5;

  metricsData.forEach((metric, index) => {
    const x = MARGIN + index * colWidth;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(metric.label, x, afterHeaderY);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(metric.value, x, afterHeaderY + 16);
  });

  return afterHeaderY + 35;
}

/**
 * Draws AI summary section
 * @param pdf - jsPDF document instance
 * @param title - Section title
 * @param content - Summary content text
 * @param yPos - Current Y position
 * @returns New Y position after summary section
 */
function drawAiSummarySection(
  pdf: import("jspdf").jsPDF,
  title: string,
  content: string | null,
  yPos: number
): number {
  if (!content) return yPos;

  const afterPageBreakY = checkPageBreak(pdf, yPos, 100);
  const afterHeaderY = drawSectionHeader(pdf, title, afterPageBreakY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...TEXT_COLOR);

  const afterTextY = drawWrappedText(
    pdf,
    content,
    MARGIN,
    afterHeaderY,
    CONTENT_WIDTH
  );
  return afterTextY + SECTION_GAP;
}

/**
 * Draws scouting reports section (max 4 reports)
 * @param pdf - jsPDF document instance
 * @param reports - Scouting reports array
 * @param yPos - Current Y position
 * @returns New Y position after reports section
 */
function drawScoutingReportsSection(
  pdf: import("jspdf").jsPDF,
  reports: readonly ScoutingReport[],
  yPos: number
): number {
  if (reports.length === 0) return yPos;

  const afterPageBreakY = checkPageBreak(pdf, yPos, 120);
  const afterHeaderY = drawSectionHeader(
    pdf,
    "SCOUTING REPORTS",
    afterPageBreakY
  );

  const displayReports = reports.slice(0, 4);
  const ROW_HEIGHT = 28;

  displayReports.forEach((report, index) => {
    const rowY = afterHeaderY + index * ROW_HEIGHT;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(`${report.reportType}`, MARGIN, rowY);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(
      `${formatPdfDate(report.date)}  |  ${report.displayText}  |  by ${report.author}`,
      MARGIN,
      rowY + 12
    );
  });

  return afterHeaderY + displayReports.length * ROW_HEIGHT + SECTION_GAP - 10;
}

/**
 * Draws notes section (max 6 notes)
 * @param pdf - jsPDF document instance
 * @param notes - Player notes array
 * @param yPos - Current Y position
 * @returns New Y position after notes section
 */
function drawNotesSection(
  pdf: import("jspdf").jsPDF,
  notes: readonly PlayerNote[],
  yPos: number
): number {
  if (notes.length === 0) return yPos;

  const afterPageBreakY = checkPageBreak(pdf, yPos, 150);
  const afterHeaderY = drawSectionHeader(pdf, "NOTES", afterPageBreakY);

  const displayNotes = notes.slice(0, 6);
  const ROW_HEIGHT = 45;

  displayNotes.forEach((note, index) => {
    const noteY = afterHeaderY + index * ROW_HEIGHT;
    const adjustedY = checkPageBreak(pdf, noteY, 50);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...PRIMARY_COLOR);
    pdf.text(note.category, MARGIN, adjustedY);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(`  |  ${formatPdfDate(note.date)}`, MARGIN + 70, adjustedY);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...TEXT_COLOR);
    drawWrappedText(
      pdf,
      note.content,
      MARGIN,
      adjustedY + 14,
      CONTENT_WIDTH,
      12
    );
  });

  return afterHeaderY + displayNotes.length * ROW_HEIGHT + SECTION_GAP;
}

/**
 * Draws individual metrics section in a compact grid
 * @param pdf - jsPDF document instance
 * @param metrics - Individual metrics array
 * @param yPos - Current Y position
 * @returns New Y position after metrics section
 */
function drawIndividualMetricsSection(
  pdf: import("jspdf").jsPDF,
  metrics: readonly IndividualMetricPdfData[],
  yPos: number
): number {
  if (metrics.length === 0) return yPos;

  const afterPageBreakY = checkPageBreak(pdf, yPos, 100);
  const afterHeaderY = drawSectionHeader(
    pdf,
    "INDIVIDUAL METRICS",
    afterPageBreakY
  );

  const COLS = 4;
  const colWidth = CONTENT_WIDTH / COLS;
  const ROW_HEIGHT = 35;

  metrics.forEach((metric, index) => {
    const col = index % COLS;
    const row = Math.floor(index / COLS);
    const x = MARGIN + col * colWidth;
    const baseY = afterHeaderY + row * ROW_HEIGHT;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(metric.label, x, baseY);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(String(metric.value), x, baseY + 18);
  });

  const totalRows = Math.ceil(metrics.length / COLS);
  return afterHeaderY + totalRows * ROW_HEIGHT + SECTION_GAP;
}

/**
 * Draws chart image section
 * @param pdf - jsPDF document instance
 * @param title - Chart section title
 * @param imageData - Base64 encoded image data
 * @param yPos - Current Y position
 * @returns New Y position after chart section
 */
function drawChartSection(
  pdf: import("jspdf").jsPDF,
  title: string,
  imageData: string | null,
  yPos: number
): number {
  if (!imageData) return yPos;

  const chartHeight = 200;
  const afterPageBreakY = checkPageBreak(pdf, yPos, chartHeight + 40);
  const afterHeaderY = drawSectionHeader(pdf, title, afterPageBreakY);

  try {
    const chartWidth = CONTENT_WIDTH;
    pdf.addImage(
      imageData,
      "PNG",
      MARGIN,
      afterHeaderY,
      chartWidth,
      chartHeight
    );
    return afterHeaderY + chartHeight + SECTION_GAP;
  } catch {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text("Chart not available", MARGIN, afterHeaderY + 10);
    return afterHeaderY + 30;
  }
}

/**
 * Generates a PDF report for a player
 * @param data - Complete player report data including identity, metrics, summaries, and charts
 * @returns Promise that resolves when PDF is generated and download triggered
 * @example
 * ```typescript
 * await generatePlayerReportPdf({
 *   identity: { playerName: "John Doe", ... },
 *   metrics: { gpr: "85", ... },
 *   aiPlayerSummary: "Player analysis...",
 *   aiScoutingSummary: null,
 *   scoutingReports: [],
 *   notes: [{ id: "1", category: "Scout Notes", date: "Jan 15", content: "...", isPrivate: false }],
 *   individualMetrics: [{ label: "Passing", value: 85 }, { label: "Dribbling", value: 78 }],
 *   charts: { spiderChart: "data:image/png;base64,...", dotPlot: null }
 * });
 * ```
 */
export async function generatePlayerReportPdf(
  data: PlayerReportPdfData
): Promise<void> {
  if (Platform.OS !== "web") {
    console.warn("PDF generation is only supported on web platform");
    return;
  }

  // Dynamic require inside the Platform.OS === "web" guard ensures jsPDF is
  // only loaded at call time, not at module load. The bundler's tree-shaking
  // excludes this code path from native bundles via the Platform.OS guard above.

  const { jsPDF } = require("jspdf") as typeof import("jspdf");
  const pdf = new jsPDF("portrait", "pt", "a4");

  const y1 = drawReportHeader(pdf, data.identity.playerName);
  const y2 = drawIdentitySection(pdf, data.identity, y1);
  const y3 = drawDivider(pdf, y2);

  const y4 = drawMetricsSection(pdf, data.metrics, y3 + 10);
  const y5 = drawDivider(pdf, y4);

  const y6 = drawAiSummarySection(
    pdf,
    "AI PLAYER SUMMARY",
    data.aiPlayerSummary,
    y5 + 10
  );

  const y7 = drawScoutingReportsSection(pdf, data.scoutingReports, y6);

  const y8 = drawNotesSection(pdf, data.notes, y7);

  const y9 = drawAiSummarySection(
    pdf,
    "AI SCOUTING SUMMARY",
    data.aiScoutingSummary,
    y8
  );

  const y10 = drawIndividualMetricsSection(pdf, data.individualMetrics, y9);

  const y11 = drawChartSection(
    pdf,
    "PERFORMANCE SPIDER CHART",
    data.charts.spiderChart,
    y10
  );

  drawChartSection(pdf, "PLAYER COMPARISON DOT PLOT", data.charts.dotPlot, y11);

  const fileName = `${data.identity.playerName.replace(/\s+/g, "_")}_Report.pdf`;
  pdf.save(fileName);
}

/* eslint-enable code-organization/enforce-statement-order -- End of procedural PDF generation code */
